import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { finalize } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { HeaderComponent } from '../../common/header/header.component';
import { SidebarComponent } from '../../common/slidebar/sidebar.components';
import { PaginatorComponent } from '../../common/paginator/paginator.component';
import { OsintSearchService } from '../../../services/osint-search/osint-search.service';
import { UserAccountService } from '../../../services/user-account/user-account.service';
import { UtilsService } from '../../../services/utils/utils.service';
import { CustomValidator } from '../../../services/custom-validator/custom-validator.service';
import { SearchServiceStatusService } from '../../../services/search-service-status/search-service-status.service';
import { SearchHistoryService } from '../../../services/search-history/search-history.service';
import { DocumentExportService } from '../../../services/document-export/document-export.service';
import { FaceSearchTabsComponent } from '../face-search-tabs/face-search-tabs.component';
import { ImageScanLoaderComponent } from '../osint-image-search-result/image-scan-loader/image-scan-loader.component';
import { CreditsLabelPipe } from '../../../pipes/credits-label.pipe';

@Component({
  selector: 'app-osint-image-search',
  standalone: true,
  imports: [CommonModule, FormsModule, HeaderComponent, SidebarComponent, FaceSearchTabsComponent, ImageScanLoaderComponent, PaginatorComponent, CreditsLabelPipe],
  templateUrl: './osint-image-search.component.html',
  styleUrls: ['./osint-image-search.component.css'],
})
export class OsintImageSearchComponent implements OnInit {
  accessDetails: any;
  activeTab: string = 'image';
  availableCredits: number | null = null;
  creditsLoading = true;

  imagePreview: string | null = null;
  imageUrl: string = '';
  uploadedFileName: string = '';
  uploadedFileSizeText: string = '';

  showDialog: boolean = false;
  isExtractingFaces: boolean = false;
  isStartingSearch: boolean = false;

  extractedImages: any;
  selectedImageIndex: number | null = null;
  selectedImageDetails: any;
  isImageMirrored: boolean = false;
  isDraggingOver: boolean = false;
  isServiceDisabled: boolean = false;
  searchCreditCost: number | null = null;

  loadingRecentResults = false;
  recentResults: any[] = [];
  recentResultsPaged: any[] = [];
  recentResultsCount = 0;
  recentResultsPerPage = 5;
  recentResultsCurrentPage = 1;
  recentResultsHasPreviousPage = false;
  recentResultsHasNextPage = false;
  recentResultsSelectAll = { isSelected: false };

  selectedFilterType = 'search-name';
  filterTypes = [
    { label: 'Search name', value: 'search-name' },
    { label: 'Notes', value: 'notes' },
    { label: 'Date Added', value: 'date-added' },
  ];
  filterKey = '';
  filterValue = '';
  searchNameFilter = '';
  notesFilter = '';
  startDateFilter = '';
  endDateFilter = '';

  get recentResultsPagedStartAndEnd() {
    if (!this.recentResults?.length || !this.recentResultsCount) {
      return { start: 0, end: 0 };
    }

    const start =
      (this.recentResultsCurrentPage - 1) * this.recentResultsPerPage + 1;
    const end = Math.min(
      start + this.recentResults.length - 1,
      this.recentResultsCount,
    );

    return { start, end };
  }

  get selectedRecentResults() {
    return this.recentResultsPaged
      .filter((recentResultPaged: any) => recentResultPaged.isSelected)
      .map((recentResultPaged: any) => recentResultPaged.search_id);
  }

  constructor(
    private router: Router,
    private osintSearchService: OsintSearchService,
    private userAccountService: UserAccountService,
    private utilsService: UtilsService,
    private toastr: ToastrService,
    private searchServiceStatusService: SearchServiceStatusService,
    private searchHistoryService: SearchHistoryService,
    private documentExportService: DocumentExportService,
  ) {}

  ngOnInit() {
    this.accessDetails = this.retrieveAccessDetails();

    // Check if OSINT service is disabled
    this.searchServiceStatusService.searchServices$.subscribe((searchServices) => {
      if (searchServices) {
        this.isServiceDisabled = !searchServices.face;
      }
    });

    this.searchServiceStatusService.creditCosts$.subscribe((cc) => {
      this.searchCreditCost = this.searchServiceStatusService.resolveCreditCost(
        cc,
        'searchCreditCount',
      );
    });

    this.getAvailableCredits();
    this.getRecentResults();

    const imageSearchState = history.state;
    if (imageSearchState?.imageUrl) {
      this.imageUrl = String(imageSearchState.imageUrl);
      this.onUrlSearch();
    }
    if (imageSearchState?.imagePreview) {
      this.imagePreview = String(imageSearchState.imagePreview);
      this.uploadedFileName =
        String(imageSearchState.uploadedFileName || '').trim() ||
        'osint-result-image';
      this.uploadedFileSizeText = 'From OSINT result';
    }
    if (imageSearchState?.imageUrl || imageSearchState?.imagePreview) {
      history.replaceState(
        { ...history.state, imageUrl: null, imagePreview: null },
        '',
      );
    }
  }

  retrieveAccessDetails() {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : null;

    return userPortal?.accessDetails || null;
  }

  setTab(tab: string) {
    this.activeTab = tab;

    switch (tab) {
      case 'image':
        this.router.navigate(['/osint-image-search']);
        break;
      case 'number':
        this.router.navigate(['/osint-number-search']);
        break;
      case 'email':
        this.router.navigate(['/osint-email-search']);
        break;
      case 'username':
        this.router.navigate(['/osint-username-search']);
        break;
      case 'name':
        this.router.navigate(['/osint-name-search']);
        break;
      case 'wallet':
        this.router.navigate(['/osint-wallet-search']);
        break;
    }
  }

  getAvailableCredits() {
    this.creditsLoading = true;
    this.userAccountService.getUserProfile().subscribe({
      next: (response) => {
        // console.log('GET USER PROFILE RESPONSE: ', response);

        const subscription = response?.data?.subscriptions?.[0] || {};
        this.availableCredits = this.userAccountService.pickAvailableCredits(response);
        this.creditsLoading = false;
      },
      error: (error) => {
        console.error(error);
      
        this.availableCredits = 0;
        this.creditsLoading = false;
      },
      complete: () => {},
    });
  }

  async onImageSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];

    if (!file) {
      return;
    }

    await this.processImageFile(file);

    // Clear the file input after processing
    (event.target as HTMLInputElement).value = '';
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = false;
  }

  async onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = false;

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) {
      return;
    }

    // Only process the first file if multiple files are dropped
    const file = files[0];
    await this.processImageFile(file);
  }

  private async processImageFile(file: File): Promise<void> {
    if (!file) {
      return;
    }

    // Validate the file using CustomValidator
    const validationErrors = await CustomValidator.validateImageFile(file);

    if (validationErrors) {
      // Get the first error message
      const errorKey = Object.keys(validationErrors)[0];
      const errorMessage = validationErrors[errorKey] as string;
      // Show error using toastr
      this.toastr.error(errorMessage, 'Image Validation Failed');
      return;
    }

    // If validation passes, proceed with reading the file
    const reader = new FileReader();
    reader.onload = () => {
      this.imagePreview = reader.result as string;
      this.uploadedFileName = file.name || 'uploaded-image';
      this.uploadedFileSizeText = this.formatFileSize(file.size);
    };
    reader.onerror = () => {
      this.toastr.error(
        'Error reading the file. Please try again.',
        'File Read Error',
      );
    };
    reader.readAsDataURL(file);
  }

  onUrlSearch() {
    const trimmedUrl = this.imageUrl?.trim();

    if (!trimmedUrl || trimmedUrl.length === 0) {
      this.toastr.error('Please enter a URL to search', 'URL Required');
      return;
    }

    if (!this.isValidUrl(trimmedUrl)) {
      this.toastr.error(
        'Please enter a valid URL (e.g., https://example.com/image.jpg)',
        'Invalid URL',
      );
      return;
    }

    this.imagePreview = trimmedUrl;
    this.uploadedFileName = this.getFileNameFromUrl(trimmedUrl) || 'url-image';
    this.uploadedFileSizeText = 'URL image';
  }

  private isValidUrl(url: string): boolean {
    try {
      if (url.startsWith('data:image/')) {
        return true;
      }
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  }

  removeImage() {
    this.imagePreview = null;
    this.imageUrl = '';
    this.uploadedFileName = '';
    this.uploadedFileSizeText = '';
  }

  private formatFileSize(bytes: number): string {
    if (!bytes || bytes <= 0) {
      return '0 KB';
    }

    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    if (unitIndex === 0) {
      return `${Math.round(size)} ${units[unitIndex]}`;
    }

    return `${size.toFixed(1)} ${units[unitIndex]}`;
  }

  private getFileNameFromUrl(url: string): string {
    try {
      const parsedUrl = new URL(url);
      const pathname = parsedUrl.pathname || '';
      const fileName = pathname.split('/').pop() || '';
      return fileName || parsedUrl.hostname;
    } catch {
      return '';
    }
  }

  extractFaces() {
    // console.log('SELECTED IMAGE: ', this.imagePreview);
    // console.log('SELECTED IMAGE URL: ', this.imageUrl);

    let payload = {};
    if (this.imageUrl) {
      payload = { url: this.imageUrl };
    } else {
      payload = { file: this.imagePreview };
    }

    // console.log('PAYLOAD: ', payload);

    this.isExtractingFaces = true;

    this.osintSearchService
      .extractFacesFromImage(payload)
      .pipe(
        finalize(() => {
          this.isExtractingFaces = false;
        }),
      )
      .subscribe({
        next: (response) => {
          // console.log('EXTRACT FACES FROM IMAGE RESPONSE: ', response);

          this.showDialog = true;
          this.extractedImages = response?.data?.faces;
        },
        error: (error) => {
          console.error(error);
          this.showDialog = false;

          let errorMessage = 'An error occurred while processing your request';

          if (error?.error?.message_key) {
            errorMessage = error.error.message_key;
          } else if (error?.error?.message) {
            errorMessage = error.error.message;
          } else if (error?.message) {
            errorMessage = error.message;
          } else if (error?.statusText) {
            errorMessage = error.statusText;
          }

          this.toastr.error(errorMessage, 'Search Failed');
        },
        complete: () => {},
      });
  }

  selectExtractedImage(index: number) {
    this.selectedImageIndex = index;
    this.selectedImageDetails = this.extractedImages[index];
    this.isImageMirrored = false;
  }

  async onSearchFaceClick() {
    let imageDetailsToSend = { ...this.selectedImageDetails };

    if (this.isImageMirrored && this.selectedImageDetails?.thumbnail) {
      try {
        const mirroredImageUrl = await this.utilsService.createMirroredImage(
          this.selectedImageDetails.thumbnail,
        );
        imageDetailsToSend.thumbnail = mirroredImageUrl;
      } catch (error) {
        console.error('Error creating mirrored image:', error);
      }
    }

    this.isStartingSearch = true;
    this.router.navigate(['/osint-image-search-result'], {
      state: {
        selectedImageDetails: {
          ...imageDetailsToSend,
          originalFileName: this.uploadedFileName,
        },
        searchName: this.uploadedFileName || 'Face Search',
      },
    });
  }

  closeDialog() {
    this.showDialog = false;
  }

  onBuyCreditsButtonClick() {
    this.router.navigate(['/plan-pricing']);
  }

  isHistoryCompleted(result: any): boolean {
    return result?.status === 'success' || result?.done === true;
  }

  getRecentResults() {
    this.loadingRecentResults = true;

    this.searchHistoryService
      .getImageHistories(
        this.recentResultsCurrentPage,
        this.recentResultsPerPage,
        this.filterKey,
        this.filterValue,
        this.startDateFilter,
        this.endDateFilter,
      )
      .subscribe({
        next: (response) => {
          this.loadingRecentResults = false;
          this.recentResults = response?.data?.data_list || [];
          this.recentResultsPaged = this.addAdditionalKeys(this.recentResults);
          this.recentResultsCount = response?.data?.total_items || 0;
          this.recentResultsHasPreviousPage = response?.data?.has_previous;
          this.recentResultsHasNextPage = response?.data?.has_next;
        },
        error: (error) => {
          console.error(error);
          this.loadingRecentResults = false;
          this.toastr.error(
            this.readErrorMessage(error, 'Failed to load recent search results'),
            'Load Results Failed',
          );
        },
      });
  }

  addAdditionalKeys(recentResults: any[]) {
    return (recentResults || []).map((recentResult: any) => ({
      ...recentResult,
      isSelected: false,
    }));
  }

  onRecentResultPagedClick(event: Event, recentResultPaged: any) {
    const checkbox = event.target as HTMLInputElement;
    this.updateRecentResultPaged(checkbox.checked, recentResultPaged.search_id);
  }

  updateRecentResultPaged(isChecked: boolean, searchId: string) {
    const recentResultsPagedToUpdate = this.recentResultsPaged.find(
      (recentResultPaged: any) => recentResultPaged.search_id === searchId,
    );

    if (recentResultsPagedToUpdate) {
      recentResultsPagedToUpdate.isSelected = isChecked;
    }
  }

  onRecentResultsPageChange(page: number) {
    this.recentResultsCurrentPage = page;
    this.recentResultsSelectAll.isSelected = false;
    this.getRecentResults();
  }

  onRecentResultsSelectAllClick() {
    this.recentResultsSelectAll.isSelected =
      !this.recentResultsSelectAll.isSelected;

    this.recentResultsPaged = this.recentResultsPaged.map(
      (recentResultPaged: any) => ({
        ...recentResultPaged,
        isSelected: this.recentResultsSelectAll.isSelected,
      }),
    );
  }

  goToSearchResultPage(searchResult: any) {
    this.router.navigate(['/osint-image-search-result'], {
      state: {
        staleSearchId: searchResult.search_id,
        isStaleResult: true,
        selectedImageDetails: {
          thumbnail: searchResult.thumbnail,
          fid: searchResult.search_id,
        },
        jobId: searchResult.job_id,
        searchName: this.getHistorySearchLabel(searchResult),
      },
    });
  }

  onFilterTypeChange() {
    this.resetHistoryFilters();
    this.getRecentResults();
  }

  resetHistoryFilters() {
    this.recentResultsCurrentPage = 1;
    this.filterKey = '';
    this.filterValue = '';
    this.searchNameFilter = '';
    this.notesFilter = '';
    this.startDateFilter = '';
    this.endDateFilter = '';
  }

  onSearchNameFilterChange(searchName: string) {
    const trimmed = (searchName || '').trim();
    this.recentResultsCurrentPage = 1;

    if (trimmed === '') {
      this.filterKey = '';
      this.filterValue = '';
      this.getRecentResults();
      return;
    }

    if (trimmed.length === 1) {
      return;
    }

    this.filterKey = 'job_name';
    this.filterValue = trimmed;
    this.getRecentResults();
  }

  onNotesFilterChange(notes: string) {
    const trimmed = (notes || '').trim();
    this.recentResultsCurrentPage = 1;

    if (trimmed === '') {
      this.filterKey = '';
      this.filterValue = '';
      this.getRecentResults();
      return;
    }

    if (trimmed.length === 1) {
      return;
    }

    this.filterKey = 'notes';
    this.filterValue = trimmed;
    this.getRecentResults();
  }

  onStartDateFilterChange(startDate: string) {
    this.startDateFilter = startDate;
    this.applyDateFilter();
  }

  onEndDateFilterChange(endDate: string) {
    this.endDateFilter = endDate;
    this.applyDateFilter();
  }

  private applyDateFilter() {
    this.recentResultsCurrentPage = 1;

    if (this.startDateFilter === '' && this.endDateFilter === '') {
      this.filterKey = '';
      this.filterValue = '';
      this.getRecentResults();
      return;
    }

    if (this.startDateFilter && this.endDateFilter) {
      this.filterKey = 'created_on';
      this.getRecentResults();
    }
  }

  onDeleteButtonClick() {
    if (!this.selectedRecentResults.length) {
      this.toastr.info('Select at least one search to delete');
      return;
    }

    this.searchHistoryService
      .deleteImageHistories(this.selectedRecentResults)
      .subscribe({
        next: () => {
          this.recentResultsSelectAll.isSelected = false;
          this.updateRecentResultsPagedAfterDelete();
          this.getRecentResults();
        },
        error: (error) => {
          console.error(error);
          this.toastr.error(
            this.readErrorMessage(error, 'Failed to delete selected results'),
            'Delete Failed',
          );
        },
      });
  }

  updateRecentResultsPagedAfterDelete() {
    this.recentResultsPaged = this.recentResultsPaged.filter(
      (recentResultPaged: any) =>
        !this.selectedRecentResults.includes(recentResultPaged.search_id),
    );

    if (this.recentResultsPaged.length === 0 && this.recentResultsHasPreviousPage) {
      this.recentResultsCurrentPage = Math.max(1, this.recentResultsCurrentPage - 1);
    }
  }

  onExportExcelButtonClick() {
    const rows = this.transformRecentResultsPaged();
    if (!rows.length) {
      this.toastr.info('No recent results to export');
      return;
    }

    this.documentExportService.exportToExcel(
      rows,
      this.buildExportFileName('face-search-results'),
      'Face Search Results',
    );
  }

  onExportCsvButtonClick() {
    const rows = this.transformRecentResultsPaged();
    if (!rows.length) {
      this.toastr.info('No recent results to export');
      return;
    }

    this.documentExportService.exportToCsv(
      rows,
      this.buildExportFileName('face-search-results'),
      'Face Search Results',
    );
  }

  onExportPdfButtonClick() {
    const rows = this.transformRecentResultsPaged();
    if (!rows.length) {
      this.toastr.info('No recent results to export');
      return;
    }

    this.documentExportService.exportResultsToPdf(
      rows,
      this.buildExportFileName('face-search-results'),
      'Face search results generated from Ryno',
      this.getUsername(),
    );
  }

  transformRecentResultsPaged() {
    return this.recentResultsPaged.map((recentResultPaged: any) => ({
      'SEARCH ID': recentResultPaged.search_id,
      'JOB ID': recentResultPaged.job_id,
      'SEARCH NAME': this.getHistorySearchLabel(recentResultPaged),
      STATUS: this.isHistoryCompleted(recentResultPaged) ? 'Completed' : 'Pending',
      NOTES: recentResultPaged.notes ?? '',
      'CREATED ON': this.utilsService.formatDateTime(
        recentResultPaged.created_on,
        'MMM d, y, h:mm:ss a',
      ),
    }));
  }

  getHistorySearchLabel(row: any): string {
    const jobName = String(row?.job_name || '').trim();
    const fileName = this.getReadableFileName(row?.file_name);

    if (jobName && !this.isGenericSearchName(jobName)) {
      return jobName;
    }

    if (fileName) {
      return fileName;
    }

    return 'Face Search';
  }

  getHistoryNotesLabel(row: any): string {
    const notes = String(row?.notes || '').trim();
    if (!notes) {
      return '';
    }

    if (notes.toLowerCase() === 'new search') {
      return 'Face Search';
    }

    return notes;
  }

  truncateFileName(fileName: string | null | undefined, maxLength: number = 28): string {
    const name = String(fileName || '').trim();
    if (!name || name.length <= maxLength) {
      return name;
    }

    const lastDot = name.lastIndexOf('.');
    const extension = lastDot > 0 && lastDot >= name.length - 8 ? name.slice(lastDot) : '';
    const ellipsis = '...';
    const baseMax = maxLength - extension.length - ellipsis.length;

    if (baseMax < 4) {
      return `${name.slice(0, Math.max(1, maxLength - ellipsis.length))}${ellipsis}`;
    }

    return `${name.slice(0, baseMax)}${ellipsis}${extension}`;
  }

  private isGenericSearchName(name: string): boolean {
    return ['new search', 'face search'].includes(name.trim().toLowerCase());
  }

  private getReadableFileName(fileName: string | null | undefined): string {
    const name = String(fileName || '').trim();
    if (!name) {
      return '';
    }

    const baseName = name.split(/[\\/]/).pop() || name;
    if (/^file_\d+\.[a-z0-9]+$/i.test(baseName)) {
      return '';
    }

    return baseName;
  }

  truncateToWords(text: string | null | undefined, maxWords: number = 20): string {
    if (text == null || text === '') {
      return '';
    }

    const words = text.trim().split(/\s+/);
    if (words.length <= maxWords) {
      return text;
    }

    return words.slice(0, maxWords).join(' ') + ' .....';
  }

  private buildExportFileName(prefix: string): string {
    const timestamp = this.utilsService.formatDateTime(
      new Date().toISOString(),
      'dd-MM-yyyy_HH-mm-ss',
    );
    return `${prefix}-${timestamp || 'export'}`;
  }

  private getUsername(): string {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : null;
    return userPortal?.loginDetails?.user_name || '';
  }

  private readErrorMessage(error: any, fallback: string): string {
    return (
      error?.error?.message_key ||
      error?.error?.message ||
      error?.message ||
      error?.statusText ||
      fallback
    );
  }
}
